import { ExternalLink, FileText } from 'lucide-react';
import React from 'react';
import { Citation as CitationType } from '../types';

interface CitationProps {
    citation: CitationType;
    index: number;
}

export const Citation: React.FC<CitationProps> = ({ citation }) => {
    const getS3ConsoleUrl = (s3Uri: string): string => {
        try {
            const bucketName = import.meta.env.VITE_S3_BUCKET_NAME;
            if (!bucketName) {
                console.error('VITE_S3_BUCKET_NAME environment variable is not set');
                return '#';
            }
            let bucket = bucketName;
            let key = s3Uri;

            const s3Match = s3Uri.match(/s3:\/\/([^/]+)\/(.+)/);
            if (s3Match) {
                bucket = s3Match[1];
                key = s3Match[2];
            } else if (!s3Uri.startsWith('s3://')) {
                key = s3Uri;
            }

            const region = 'us-east-1';
            return `https://s3.console.aws.amazon.com/s3/object/${bucket}?region=${region}&prefix=${encodeURIComponent(key)}`;
        } catch (e) {
            console.error('Error parsing S3 URI:', e);
        }
        return '#';
    };

    const getFileName = (s3Uri: string): string => {
        try {
            const parts = s3Uri.split('/');
            return parts[parts.length - 1] || citation.title;
        } catch (e) {
            return citation.title;
        }
    };

    const displayTitle = citation.title || getFileName(citation.s3_uri);

    const handleViewDocument = () => {
        const url = getS3ConsoleUrl(citation.s3_uri);
        console.log('S3 URI:', citation.s3_uri);
        console.log('Generated URL:', url);

        if (url !== '#') {
            const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
            if (!newWindow) {
                alert(`Please allow pop-ups to view the document.\n\nDocument: ${displayTitle}\nURL: ${url}`);
            }
        } else {
            alert(`Unable to generate S3 link.\n\nDocument: ${displayTitle}\nS3 URI: ${citation.s3_uri}`);
        }
    };

    const highlightTerms = (text: string): JSX.Element => {
        if (!text) return <>{text}</>;

        const acronyms = text.match(/\b[A-Z]{4,}\b/g) || [];

        const partNumbers = text.match(/\b[A-Z]{2,4}\d{2,6}\b/g) || [];
        const contractNumberPattern = /\b[A-Z]{3}\d{3}-\d{2}-[A-Z]\d+\b/g;
        const contractNumbers = text.match(contractNumberPattern) || [];
        const filteredPartNumbers = partNumbers.filter(pn => {
            return !contractNumbers.some(cn => cn.includes(pn));
        });

        const systemIds = text.match(/\b[A-Z]\d{3,4}\b/g) || [];

        const allTerms = [...new Set([...acronyms, ...filteredPartNumbers, ...systemIds])];

        if (allTerms.length === 0) return <>{text}</>;

        const pattern = new RegExp(`(${allTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');

        const parts = text.split(pattern);

        return (
            <>
                {parts.map((part, i) => {
                    const isHighlighted = allTerms.some(term => part === term);
                    return isHighlighted ? (
                        <mark key={i} className="citation-highlight">{part}</mark>
                    ) : (
                        <span key={i}>{part}</span>
                    );
                })}
            </>
        );
    };

    return (
        <div className="citation">
            <div className="citation-header">
                <FileText className="citation-icon" size={16} />
                <span className="citation-title">{displayTitle}</span>
                <button
                    className="citation-link"
                    onClick={handleViewDocument}
                    title="View document in S3 Console"
                >
                    <ExternalLink size={12} />
                    View
                </button>
            </div>
            {citation.excerpt && (
                <div className="citation-excerpt">
                    "{highlightTerms(citation.excerpt)}"
                </div>
            )}
            {citation.score && (
                <div className="citation-score">
                    Relevance: {(citation.score * 100).toFixed(1)}%
                </div>
            )}
        </div>
    );
};
